import React from 'react';
import { styled, Box } from '@mui/material';
import clsx from 'clsx';
import { isUndefined } from 'lodash-es';
import { IconInfoOutline } from '../../internal/icons';
import { FormContext } from './context';
import { Button } from '../../Button';
import { Tooltip } from '../../Tooltip';
import { getStyle, formClasses } from './style';
import { useTranslation } from '../../hooks/useTranslation';
import { Grid2 } from '../../Mui/Grid2';
function toTooltipProps(tooltip) {
    if (!tooltip) {
        return null;
    }
    if (typeof tooltip === 'object' && !React.isValidElement(tooltip)) {
        return tooltip;
    }
    return {
        title: tooltip,
    };
}
export function FormItemLabelBase(props) {
    const { className, label, labelWidth, labelCol, labelAlign, colon, htmlFor, required, requiredMark, tooltip, labelExtra, description, } = props;
    const { t } = useTranslation('Form');
    if (!label) {
        return null;
    }
    return (React.createElement(FormContext.Consumer, null, ({ labelCol: contextLabelCol, labelAlign: contextLabelAlign, layout, colon: contextColon, labelWrap, }) => {
        const mergedLabelCol = labelCol ?? contextLabelCol ?? {};
        const mergedLabelAlign = labelAlign ?? contextLabelAlign;
        let labelChildren = label;
        let requireMarkNode = null;
        const computedColon = colon === true || (contextColon !== false && colon !== false);
        const haveColon = computedColon && layout !== 'vertical';
        if (haveColon && typeof label === 'string' && label.trim() !== '') {
            labelChildren = label.replace(/[:|：]\s*$/, '');
        }
        if (requiredMark !== false) {
            // let requireMarkNode: React.ReactNode | null = null;
            if (!required && requiredMark === 'optional') {
                requireMarkNode = (React.createElement("span", { className: formClasses.label.optionalMark },
                    "(",
                    t('optional'),
                    ")"));
            }
            if (required) {
                requireMarkNode = (React.createElement("span", { className: formClasses.label.requiredMark, "aria-hidden": true }, "*"));
            }
        }
        // Tooltip
        let tooltipNode = null;
        const tooltipProps = toTooltipProps(tooltip);
        if (tooltipProps) {
            const { icon = React.createElement(IconInfoOutline, null), ...restTooltipProps } = tooltipProps;
            tooltipNode = (React.createElement(Tooltip, { ...restTooltipProps, arrow: true },
                React.createElement(Button, { variant: "icon", type: "tertiary", size: "small", "aria-label": typeof label === 'string'
                        ? t('learnMoreWithName', { name: `${label}` })
                        : t('learnMore'), role: "button", sx: {
                        minWidth: '20px !important',
                        width: '20px !important',
                        height: '20px !important',
                    }, className: formClasses.label.tooltip }, icon)));
        }
        const labelClassName = clsx(className, formClasses.label.label, {
            [`${formClasses.label.required}`]: required,
            [`${formClasses.label.optional}`]: !required && requiredMark !== false,
        });
        const labelColClassName = clsx(formClasses.label.root, mergedLabelCol.className, {
            [`${formClasses.label.left}`]: mergedLabelAlign === 'left',
            [`${formClasses.label.wrap}`]: !!labelWrap,
            [`${formClasses.label.right}`]: mergedLabelAlign === 'right' ||
                (isUndefined(mergedLabelAlign) && layout === 'horizontal'),
        });
        return (React.createElement(Grid2, { ...mergedLabelCol, className: labelColClassName },
            React.createElement(Box, { className: clsx(formClasses.label.container) },
                React.createElement(Box, { className: clsx(formClasses.label.wrapper, {
                        [`${formClasses.label.colon}`]: haveColon,
                    }) },
                    React.createElement(Box, { className: formClasses.label.outerWrapper },
                        React.createElement(Box, { className: formClasses.label.innerWrapper },
                            React.createElement("label", { htmlFor: htmlFor, className: labelClassName, style: { width: labelWidth } }, labelChildren),
                            requireMarkNode,
                            React.createElement("span", { className: formClasses.label.tooltipWrapper }, tooltipNode)))),
                labelExtra ? React.createElement(Box, null, labelExtra) : null),
            description && layout === 'vertical' ? (React.createElement(Box, { className: clsx(formClasses.label.description), id: `${htmlFor}-desc` }, description)) : null));
    }));
}
const StyledFormItemLabel = styled(FormItemLabelBase)((props) => {
    const { theme } = props;
    return getStyle(theme);
});
export const FormItemLabel = React.forwardRef((props, ref) => (React.createElement(StyledFormItemLabel, { ...props, ref: ref })));
